// dashboard-resolver.service.ts
import { Injectable } from '@angular/core';
import {
  DashboardDetails, WidgetData, WidgetColumnData,
  KpiCardConfig, ChartConfig, TableConfig, TableColumn, WidgetRow, WidgetDataResult, MapConfig
} from './widget.models';

export interface ResolvedWidget {
  widgetId: number;
  widgetTypeCode: string;
  gridCol: number;
  gridRow: number;
  gridWidth: number;
  gridHeight: number;
  sortOrder: number;
  kpiConfig?: KpiCardConfig;
  chartConfig?: ChartConfig;
  tableConfig?: TableConfig;
  mapConfig?: MapConfig;
  widgetIcon?: string;
  rows: WidgetRow[];
}

@Injectable({ providedIn: 'root' })
export class DashboardResolverService {
  resolve(
    details: DashboardDetails,
    widgetData: Record<number, WidgetDataResult>,
    langCode: 'en' | 'ar' = 'en',
  ): ResolvedWidget[] {
    const colors = this.parseColors(details.dashboards[0]?.chartColorArray);

    return details.widgets
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((w) => {
        const dataResult = widgetData[w.widgetId];
        const cols = details.widgetColumns.filter(
          (c) => c.widgetId === w.widgetId,
        );
        const rows = this.resolveRows(dataResult, cols);

        const code = (w.widgetTypeCode ?? '').toUpperCase();
        const title =
          langCode === 'ar'
            ? w.widgetTitleAr || w.widgetTitleEn || ''
            : w.widgetTitleEn || w.widgetTitleAr || '';

        const resolved: ResolvedWidget = {
          widgetId: w.widgetId,
          widgetTypeCode: code,
          gridCol: w.gridCol,
          gridRow: w.gridRow,
          gridWidth: w.gridWidth,
          gridHeight: w.gridHeight,
          sortOrder: w.sortOrder,
          widgetIcon: w.widgetIcon,
          rows,
        };

        switch (code) {
          case 'KPI':
            resolved.kpiConfig = this.buildKpi(w, cols, rows, langCode);
            break;
          case 'PIE':
          case 'DOUGHNUT':
            resolved.chartConfig = this.buildPieChart(
              w,
              cols,
              rows,
              colors,
              title,
              code,
            );
            break;
          case 'LINE':
            resolved.chartConfig = this.buildLineChart(
              w,
              cols,
              rows,
              colors,
              title,
            );
            break;
          case 'MULTIPLE_LINES': // ← add
            resolved.chartConfig = this.buildMultiLineChart(
              w,
              cols,
              rows,
              colors,
              title,
            ); // ← add
            break;
          case 'BAR':
          case 'HBAR':
            resolved.chartConfig = this.buildBarChart(
              w,
              cols,
              rows,
              colors,
              title,
              code === 'HBAR',
            );
            break;
          case 'STACKED_BAR':
            resolved.chartConfig = this.buildStackedBarChart(
              w,
              cols,
              rows,
              colors,
              title,
            );
            break;
          case 'TABLE':
          case 'ADVANCEDTABLE':
            resolved.tableConfig = this.buildTable(
              w,
              cols,
              rows,
              langCode,
              title,
            );
            break;
          // case 'MAP_H':
          //   resolved.mapConfig = this.buildMap(w, cols, rows, title, 'horizontal');
          //   break;
          case 'MAP_V':
            resolved.mapConfig = this.buildMap(
              w,
              cols,
              rows,
              title,
              'vertical',
            );
            break;
        }

        return resolved;
      });
  }

  // ── Row resolver — finds best-matching result set ────────────
  private resolveRows(
    dataResult: WidgetDataResult | undefined,
    cols: WidgetColumnData[],
  ): WidgetRow[] {
    if (!dataResult?.resultSets?.length) return [];

    const expectedColumns = cols
      .map((c) => c.dataSourceColumnNameEn?.toLowerCase())
      .filter(Boolean) as string[];

    if (!expectedColumns.length) return [];

    let bestRs: WidgetRow[] = [];
    let bestScore = -1;

    for (const rs of dataResult.resultSets) {
      const rows: WidgetRow[] = Array.isArray(rs)
        ? rs
        : ((rs as any).rows ?? []);
      if (!rows.length) continue;

      const rsKeys = Object.keys(rows[0]).map((k) => k.toLowerCase());
      const score = expectedColumns.filter((col) =>
        rsKeys.includes(col),
      ).length;

      if (score > bestScore) {
        bestScore = score;
        bestRs = rows;
      }
    }

    return bestRs;
  }

  // ── Role-based column helpers ─────────────────────────────────

  /** Returns the first column matching any of the given role codes */
  private colByRole(
    cols: WidgetColumnData[],
    ...roles: string[]
  ): WidgetColumnData | undefined {
    return cols.find((c) =>
      roles.includes(c.columnRoleCode?.toUpperCase() ?? ''),
    );
  }

  /** Returns ALL columns matching any of the given role codes, sorted by sortOrder */
  private colsByRole(
    cols: WidgetColumnData[],
    ...roles: string[]
  ): WidgetColumnData[] {
    return cols
      .filter((c) => roles.includes(c.columnRoleCode?.toUpperCase() ?? ''))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  // ── KPI ──────────────────────────────────────────────────────
  private buildKpi(
    widget: WidgetData,
    cols: WidgetColumnData[],
    rows: WidgetRow[],
    langCode: string,
  ): KpiCardConfig {
    // VALUE role → the numeric measure to display
    const valueCol = this.colByRole(cols, 'VALUE');
    // LABEL role → optional secondary label (fallback to widget title)
    const labelCol = this.colByRole(cols, 'LABEL');

    const val =
      rows.length && valueCol
        ? (rows[0][valueCol.dataSourceColumnNameEn ?? ''] ?? 0)
        : 0;

    const title =
      langCode === 'ar'
        ? widget.widgetTitleAr || widget.widgetTitleEn || ''
        : widget.widgetTitleEn || widget.widgetTitleAr || '';

    // If a LABEL column exists, use its first row value as the subtitle
    const labelValue =
      labelCol && rows.length
        ? rows[0][labelCol.dataSourceColumnNameEn ?? '']
        : undefined;

    return {
      title: labelValue ?? title,
      value: val,
      icon: widget.widgetIcon ?? '',
      iconBackgroundColor: 'bg-primary',
      width: '100%',
      height: '100%',
    };
  }

  // ── PIE / DOUGHNUT ───────────────────────────────────────────
  private buildPieChart(
    widget: WidgetData,
    cols: WidgetColumnData[],
    rows: WidgetRow[],
    colors: string[],
    title: string,
    code: string,
  ): ChartConfig {
    // LABEL role → slice labels (dimension)
    // VALUE role → slice sizes (measure)
    const labelCol = this.colByRole(cols, 'LABEL');
    const valueCol = this.colByRole(cols, 'VALUE');

    return {
      title,
      type: code === 'DOUGHNUT' ? 'doughnut' : 'pie',
      labelKey: labelCol?.dataSourceColumnNameEn ?? 'label',
      valueKey: valueCol?.dataSourceColumnNameEn ?? 'value',
      data: rows,
      colors,
      width: '100%',
      height: this.gridToHeight(widget.gridHeight),
      showLegend: true,
    };
  }

  // ── LINE ─────────────────────────────────────────────────────
  private buildLineChart(
    widget: WidgetData,
    cols: WidgetColumnData[],
    rows: WidgetRow[],
    colors: string[],
    title: string,
  ): ChartConfig {
    // X_AXIS role → horizontal labels
    // Y_AXIS role → one or more lines
    // SERIES role → group/color dimension (future use)
    const xCol = this.colByRole(cols, 'X_AXIS');
    const yAxisCols = this.colsByRole(cols, 'Y_AXIS');

    return {
      title,
      type: 'line',
      labelKey: xCol?.dataSourceColumnNameEn ?? 'label',
      valueKey:
        yAxisCols.length > 1
          ? yAxisCols.map((c) => c.dataSourceColumnNameEn ?? '')
          : (yAxisCols[0]?.dataSourceColumnNameEn ?? 'value'),
      data: rows,
      colors,
      width: '100%',
      height: this.gridToHeight(widget.gridHeight),
      showGrid: true,
      showLegend: yAxisCols.length > 1,
      xLabel: xCol?.dataSourceColumnDisplayLabelEn,
      yLabel: yAxisCols[0]?.dataSourceColumnDisplayLabelEn,
    };
  }

  // ── BAR ──────────────────────────────────────────────────────
  private buildBarChart(
    widget: WidgetData,
    cols: WidgetColumnData[],
    rows: WidgetRow[],
    colors: string[],
    title: string,
    horizontal: boolean,
  ): ChartConfig {
    // X_AXIS role → category axis
    // Y_AXIS role → one or more bars
    // SERIES role → group dimension (used for multi-series grouping)
    const xCol = this.colByRole(cols, 'X_AXIS');
    const yAxisCols = this.colsByRole(cols, 'Y_AXIS');
    const seriesCol = this.colByRole(cols, 'SERIES');
    const sortCol = this.colByRole(cols, 'SORT'); 

    // If SERIES column exists, pivot rows to produce one dataset per series value
    const { data, valueKey } = seriesCol
      ? this.pivotBySeries(rows, xCol, yAxisCols[0], seriesCol, sortCol)
      : {
          data: rows,
          valueKey:
            yAxisCols.length > 1
              ? yAxisCols.map((c) => c.dataSourceColumnNameEn ?? '')
              : (yAxisCols[0]?.dataSourceColumnNameEn ?? 'value'),
        };

    return {
      title,
      type: horizontal ? 'horizontalBar' : 'bar',
      labelKey: xCol?.dataSourceColumnNameEn ?? 'label',
      valueKey,
      data,
      colors,
      width: '100%',
      height: this.gridToHeight(widget.gridHeight),
      showGrid: true,
      showLegend: yAxisCols.length > 1 || !!seriesCol,
      xLabel: xCol?.dataSourceColumnDisplayLabelEn,
      yLabel: yAxisCols[0]?.dataSourceColumnDisplayLabelEn,
    };
  }

  // ── TABLE ─────────────────────────────────────────────────────
  private buildTable(
    widget: WidgetData,
    cols: WidgetColumnData[],
    rows: WidgetRow[],
    langCode: string,
    title: string,
  ): TableConfig {
    // COLUMN role → visible table columns
    // SORT   role → default sort column
    const visibleCols = this.colsByRole(cols, 'COLUMN');
    const sortCol = this.colByRole(cols, 'SORT');

    // Fallback: if no COLUMN-role cols configured, show all cols
    const displayCols = visibleCols.length ? visibleCols : cols;

    const tableCols: TableColumn[] = displayCols.map((c) => ({
      key: c.dataSourceColumnNameEn ?? '',
      labelEn:
        c.dataSourceColumnDisplayLabelEn ?? c.dataSourceColumnNameEn ?? '',
      labelAr:
        c.dataSourceColumnDisplayLabelAr ?? c.dataSourceColumnNameAr ?? '',
      sortable: true,
      type:
        c.dataTypeId === 5 || c.dataTypeId === 6
          ? 'date'
          : c.dataTypeId === 2
            ? 'number'
            : 'text',
    }));

    return {
      title,
      columns: tableCols,
      data: rows,
      pageSize: 50,
      pageSizeOptions: [5, 10, 25, 50],
      showFilter: true,
      langCode,
      sortActive: sortCol?.dataSourceColumnNameEn,
      sortDirection: 'asc',
      width: '100%',
      height: this.gridToHeight(widget.gridHeight),
    };
  }

  private buildMultiLineChart(
    widget: WidgetData,
    cols: WidgetColumnData[],
    rows: WidgetRow[],
    colors: string[],
    title: string,
  ): ChartConfig {
    const xCol = this.colByRole(cols, 'X_AXIS');
    const yAxisCols = this.colsByRole(cols, 'Y_AXIS');
    const seriesCol = this.colByRole(cols, 'SERIES');

    const { data, valueKey } = seriesCol
      ? this.pivotBySeries(rows, xCol, yAxisCols[0], seriesCol)
      : {
          data: rows,
          valueKey: yAxisCols.map((c) => c.dataSourceColumnNameEn ?? ''),
        };

    return {
      title,
      type: 'line',
      labelKey: xCol?.dataSourceColumnNameEn ?? 'label',
      valueKey,
      data,
      colors,
      width: '100%',
      height: this.gridToHeight(widget.gridHeight),
      showGrid: true,
      showLegend: true,
      xLabel: xCol?.dataSourceColumnDisplayLabelEn,
      yLabel: yAxisCols[0]?.dataSourceColumnDisplayLabelEn,
    };
  }

  // ── SERIES pivot helper ───────────────────────────────────────
  /**
   * Pivots flat rows into one column per unique series value.
   * Input:  [{ month:'Jan', dept:'HR',  sales:100 },
   *          { month:'Jan', dept:'IT',  sales:200 },
   *          { month:'Feb', dept:'HR',  sales:150 }]
   * Output: [{ month:'Jan', HR:100, IT:200 },
   *          { month:'Feb', HR:150, IT:0   }]
   */

  private applySort(data: WidgetRow[], sortKey?: string): WidgetRow[] {
  if (!sortKey) return data;
  return [...data].sort((a, b) => {
    const av = Number(a[sortKey]), bv = Number(b[sortKey]);
    return !isNaN(av) && !isNaN(bv) ? av - bv : String(a[sortKey]).localeCompare(String(b[sortKey]));
  });
}
  private pivotBySeries(
    rows: WidgetRow[],
    xCol: WidgetColumnData | undefined,
    yCol: WidgetColumnData | undefined,
    seriesCol: WidgetColumnData,
    sortCol?: WidgetColumnData,
  ): { data: WidgetRow[]; valueKey: string[] } {
    const xKey = xCol?.dataSourceColumnNameEn ?? 'label';
    const yKey = yCol?.dataSourceColumnNameEn ?? 'value';
    const seriesKey = seriesCol.dataSourceColumnNameEn ?? 'series';
    const sortKey = sortCol?.dataSourceColumnNameEn;



    const isCountAggregation = (yCol?.aggregation ?? '').toUpperCase() === 'COUNT';


    // Collect unique series values
    const seriesValues = [
      ...new Set(rows.map((r) => String(r[seriesKey] ?? ''))),
    ];

    // Group rows by x value
    const xMap = new Map<string, WidgetRow>();
    for (const row of rows) {
      const xVal = String(row[xKey] ?? '');
      // if (!xMap.has(xVal)) xMap.set(xVal, { [xKey]: xVal });
      // const pivot = xMap.get(xVal)!;
      // pivot[String(row[seriesKey])] = row[yKey] ?? 0;

      if (!xMap.has(xVal)) {
        const initRow: WidgetRow = { [xKey]: xVal };
        if (sortKey) initRow[sortKey] = row[sortKey];
        xMap.set(xVal, initRow);
      }
      const pivot = xMap.get(xVal)!;
          
      const rawVal = row[yKey];
      pivot[String(row[seriesKey])] =
      isCountAggregation && Number(rawVal) === 0 ? null : (rawVal ?? null);
    }

    // Fill missing series with 0
    let data = Array.from(xMap.values()).map((r) => {
      seriesValues.forEach((s) => {
        if (r[s] === undefined)  r[s] = isCountAggregation ? null : 0;
      });
      return r;
    });

    if (sortKey) data = this.applySort(data, sortKey); 
    return { data, valueKey: seriesValues };
  }

  // ── Helpers ──────────────────────────────────────────────────
  private parseColors(raw?: string): string[] {
    if (!raw)
      return [
        '#5d87ff',
        '#13deb9',
        '#ffae1f',
        '#fa896b',
        '#49beff',
        '#7460ee',
        '#ff4560',
        '#00e396',
      ];
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }

  private gridToHeight(gh: number): string {
    return `${gh * 80}px`;
  }

  // new builder method, add alongside the others:
  private buildMap(
    widget: WidgetData,
    cols: WidgetColumnData[],
    rows: WidgetRow[],
    title: string,
    orientation: 'horizontal' | 'vertical',
  ): MapConfig {
    const latCol = this.colByRole(cols, 'LAT');
    const lngCol = this.colByRole(cols, 'LNG');
    const labelCol = this.colByRole(cols, 'LABEL');
    const iconCol = this.colByRole(cols, 'ICON');
    const pinLabelCol = this.colByRole(cols, 'PIN_LABEL');

    return {
      title,
      data: rows,
      latKey: latCol?.dataSourceColumnNameEn ?? 'lat',
      lngKey: lngCol?.dataSourceColumnNameEn ?? 'lng',
      labelKey: labelCol?.dataSourceColumnNameEn,
      iconKey: iconCol?.dataSourceColumnNameEn,
      pinLabelKey: pinLabelCol?.dataSourceColumnNameEn,
      orientation,
      width: '100%',
      height: this.gridToHeight(widget.gridHeight),
      // zoom: 12,
      bounds: {
        southwest: { lat: 21.589334, lng: 39.105589 },
        northeast: { lat: 21.62528, lng: 39.107503 },
      },
    };
  }

  private buildStackedBarChart(
    widget: WidgetData,
    cols: WidgetColumnData[],
    rows: WidgetRow[],
    colors: string[],
    title: string,
  ): ChartConfig {
    const xCol = this.colByRole(cols, 'X_AXIS');
    const yAxisCols = this.colsByRole(cols, 'Y_AXIS');
    const seriesCol = this.colByRole(cols, 'SERIES');

    const { data, valueKey } = seriesCol
      ? this.pivotBySeries(rows, xCol, yAxisCols[0], seriesCol)
      : {
          data: rows,
          valueKey: yAxisCols.map((c) => c.dataSourceColumnNameEn ?? ''),
        };

    return {
      title,
      type: 'groupedBar',
      labelKey: xCol?.dataSourceColumnNameEn ?? 'label',
      valueKey,
      data,
      colors,
      width: '100%',
      height: this.gridToHeight(widget.gridHeight),
      showGrid: true,
      showLegend: true,
      showValueLabels: true,
      minBarLength: 0, 
      xLabel: xCol?.dataSourceColumnDisplayLabelEn,
      yLabel: yAxisCols[0]?.dataSourceColumnDisplayLabelEn,
    };
  }
}

